import { useEffect, useRef, useState } from "react";
import { Headphones, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, resolveImage } from "@/lib/api";

export function ListenButton({ slug, readTime, lang = "en" }) {
  const [state, setState] = useState("idle");
  const [url, setUrl] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => { setState("idle"); setUrl(null); }, [slug, lang]);

  const start = async () => {
    setState("loading");
    try {
      const opts = { timeout: 0, params: { lang } };
      let { data } = await api.post(`/articles/${slug}/audio`, {}, opts);
      let tries = 0;
      while (data.status === "generating" && tries < 40) {
        await new Promise((r) => setTimeout(r, 3000));
        ({ data } = await api.post(`/articles/${slug}/audio`, {}, opts));
        tries += 1;
      }
      if (!data.url) throw new Error("Audio is still being prepared, please try again in a moment.");
      setUrl(resolveImage(data.url));
      setState("ready");
      setTimeout(() => audioRef.current?.play().catch(() => {}), 200);
    } catch (err) {
      setState("idle");
      toast.error(apiError(err, "Could not prepare audio"));
    }
  };

  if (state === "ready" && url) {
    return (
      <div className="flex w-full flex-col gap-2 rounded-2xl border border-[var(--line)] bg-white p-4 sm:flex-row sm:items-center" data-testid="listen-player">
        <span className="inline-flex shrink-0 items-center gap-2 text-sm font-semibold text-[var(--chakra-deep)]"><Headphones className="h-4 w-4 text-[var(--saffron-deep)]" /> Listening</span>
        <audio ref={audioRef} src={url} controls preload="auto" className="w-full" data-testid="listen-audio" />
      </div>
    );
  }

  return (
    <button
      onClick={start} disabled={state === "loading"} data-testid="listen-btn"
      className="inline-flex items-center gap-2 rounded-full bg-[var(--chakra)] px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-100 transition-all hover:bg-[var(--chakra-deep)] disabled:opacity-70"
    >
      {state === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Headphones className="h-4 w-4" />}
      {state === "loading" ? "Preparing audio (first time takes a moment)…" : lang === "hi" ? `सुनिए · Listen in Hindi · ~${readTime} min` : `Listen to this article · ~${readTime} min`}
    </button>
  );
}
